import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useSpring } from 'react-spring';
import { useDispatch, useSelector } from 'react-redux';
import useAnimations from '../../../hooks/useAnimations';
import useClickOutside from '../../../hooks/useClickOutside';
import {
  DropDownWrapper,
  DropDownInput,
  DropDownSelect,
  ListOfOptions,
  TopicOption,
  TitlePlaceholder,
  ArrowIcons,
  Checkbox,
  HiddenInput,
  DropDown,
  DropdownLabel,
  SelectedPlaceholder,
} from './vehicleDropDown.styles';
import ArrowUpIcon from '../../../assets/icons/ArrowUpIcon';
import ArrowDownIcon from '../../../assets/icons/ArrowDownIcon';
import CheckMarkIcon from '../../../assets/icons/CheckMarkIcon';
import { COLORS } from '../../../styles/variables';
import { setUserInputs, selectInputs } from '../../../redux/inputsReducer';
import { DropdownOption } from '../../../types';
import { OptionalNote } from '../CheckboxInput/CheckBoxInput.styles';
import { INPUT_TYPE } from '../../../constants';

interface VehicleDropdownProps {
  options: DropdownOption[];
  inputLabel: string;
  inputKey: string;
  isOptional?: boolean;
  dropdownHeight?: number;
}

const VehicleDropdown: React.FC<VehicleDropdownProps> = ({
  options,
  inputLabel,
  inputKey,
  isOptional,
  dropdownHeight = 250,
}) => {
  const dispatch = useDispatch();
  const inputs = useSelector(selectInputs);
  const inputValue = inputs[inputKey];
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [focused, setFocused] = useState(false);

  const isAnySelected = !!inputValue;

  const { dropdownAnimation, filtersAccordianAnimation } = useAnimations(
    isDropdownOpen,
    dropdownHeight,
  );

  useEffect(() => {
    const { modelFamilyOfInterest } = inputs;
    if (
      inputKey === INPUT_TYPE.MODEl_LINE_OF_INTEREST &&
      modelFamilyOfInterest &&
      !inputValue &&
      options.length > 0
    ) {
      dispatch(
        setUserInputs({
          inputName: inputKey,
          inputValue: options[0].value,
        }),
      );
    }
  }, [inputKey, inputValue, options, dispatch, inputs]);

  const inputAnimation = useSpring({
    to: {
      top: focused || inputValue ? -18 : 8,
      fontSize: focused || inputValue ? '12px' : '16px',
    },
  });

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = () => {
    setFocused(false);
  };

  const onHandleChange = useCallback(
    (value: string) => {
      dispatch(setUserInputs({ inputName: inputKey, inputValue: value }));

      setIsDropdownOpen(false);
    },
    [inputKey, dispatch],
  );

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent, value: string) => {
      if (e.key !== ' ') return;
      e.preventDefault();
      onHandleChange(value);
    },
    [onHandleChange],
  );

  const dropdownRef = useRef(null);

  useClickOutside(
    dropdownRef,
    useCallback(() => {
      setIsDropdownOpen(false);
    }, [setIsDropdownOpen]),
  );

  return (
    <DropDownWrapper ref={dropdownRef} isOptional={isOptional} data-testid="dropdown-wrapper">
      <DropDownInput
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        data-testid="vehicle-dropdown-trigger"
      >
        <TitlePlaceholder style={inputAnimation}>
          {inputLabel}
          {isOptional && <OptionalNote>(optional)</OptionalNote>}
        </TitlePlaceholder>
        {isAnySelected && (
          <SelectedPlaceholder>
            {options.find((option) => option.value === inputValue)?.label}
          </SelectedPlaceholder>
        )}

        <ArrowIcons>
          {isDropdownOpen ? (
            <ArrowUpIcon color={COLORS.grey60} />
          ) : (
            <ArrowDownIcon color={COLORS.grey60} />
          )}
        </ArrowIcons>
      </DropDownInput>
      {dropdownAnimation(
        (props, item) =>
          item && (
            <DropDownSelect style={props}>
              <DropDown style={filtersAccordianAnimation} data-testid="dropdown-selector">
                <ListOfOptions>
                  {options?.map((option: any) => {
                    const { value, label, key } = option;
                    const checked = inputValue === value;
                    return (
                      <TopicOption key={key} onClick={() => onHandleChange(value)}>
                        <Checkbox
                          tabIndex={0}
                          onClick={() => onHandleChange(value)}
                          role="checkbox"
                          aria-checked={checked}
                          onKeyDown={(e) => handleKeyPress(e, value)}
                          checked={checked}
                          data-testid="dropdown-option-checkbox"
                        >
                          {checked && <CheckMarkIcon />}
                        </Checkbox>
                        <HiddenInput
                          type="checkbox"
                          onChange={() => onHandleChange(value)}
                          checked={checked}
                          name={value}
                        />
                        <DropdownLabel>{label}</DropdownLabel>
                      </TopicOption>
                    );
                  })}
                </ListOfOptions>
              </DropDown>
            </DropDownSelect>
          ),
      )}
    </DropDownWrapper>
  );
};

export default VehicleDropdown;
